import { REVALIDATE_TIME } from "@/constants/cache"
import { message } from "antd"
import { AdminHttpClient } from "../app/api/AdminHttpClient"
import { API_PREFIX_WEBSITE_PATH } from "../constants/prefix-path-api"
import { FilterSearchParams } from "../lib/external-components"
import { safeReadLocale } from "../lib/safe-locale"
import { Banners } from "../types/codegen/Banners"
import { BannerRequest } from "../types/codegen/data-contracts"
import { RequestParams } from "../types/codegen/http-client"
import { ApiResponse, BannerData, BannerOptions } from "../types/types"
import {
  ensureBannerDataIntegrity,
  extractUniquePositions,
  getDefaultTargetOptions,
  getFallbackBannerOptions,
  processWebsiteOptions,
} from "../utils/banner-utils"

type GetBannersQuery = Parameters<Banners["getBanners"]>[0]
type GetBannersParams = Parameters<Banners["getBanners"]>[1]

const appendFilterToQuery = (query: Record<string, unknown>, value: unknown, prefix: string) => {
  if (value === undefined || value === null || value === "") return

  if (Array.isArray(value)) {
    value.forEach((item, index) => appendFilterToQuery(query, item, `${prefix}[${index}]`))
    return
  }

  if (typeof value === "object") {
    Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
      appendFilterToQuery(query, nestedValue, `${prefix}[${key}]`)
    })
    return
  }

  query[prefix] = value
}

const getBaseUrl = () => {
  const url = process.env.NEXT_PUBLIC_API_URL || "https://api.tamdagroup.eu/api"
  return url.endsWith("/api") ? url : `${url}/api`
}

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null
  try {
    return localStorage.getItem("auth_token")
  } catch {
    return null
  }
}

// api.tamdagroup.eu intermittently resets reused keep-alive sockets, which
// surfaces as an unhandled "fetch failed" (ECONNRESET) that fetchBanners then
// swallows — silently returning an empty banner list. Retry the transport-level
// failure a couple of times before giving up.
const RETRYABLE_FETCH_ERROR_CODES = new Set(["ECONNRESET", "ETIMEDOUT", "ECONNREFUSED", "EPIPE"])

const fetchWithRetry = async (...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => {
  const maxAttempts = 3
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fetch(...args)
    } catch (error) {
      const code = (error as { cause?: { code?: string } })?.cause?.code
      if (!code || !RETRYABLE_FETCH_ERROR_CODES.has(code) || attempt === maxAttempts) throw error
      await new Promise(resolve => setTimeout(resolve, attempt * 200))
    }
  }
  throw new Error("fetchWithRetry: unreachable")
}

export const bannersApi = new Banners({
  baseUrl: getBaseUrl(),
  customFetch: fetchWithRetry,
  securityWorker: async () => {
    const token = getAccessToken()
    if (!token) {
      return { headers: {} }
    }
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
    }
  },
})
export const fetchBanners = async (searchTerms: { [key: string]: string } = {}) => {
  const params: FilterSearchParams = {
    populate: "*",
    sort: "createdAt:desc",
    publicationState: "preview",
    filters: {},
    pagination: {
      pageSize: "100",
    },
    locale: "all",
  }

  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

  Object.keys(searchTerms).forEach(key => {
    const value = searchTerms[key]

    if (key === "sort") {
      if (value) params.sort = value
    } else if (key === "publicationState") {
      const convertParamStatusBanner = value === "draft" || value === "all" ? "preview" : "live"
      params.publicationState = convertParamStatusBanner

      if (value === "draft") {
        params.filters.publishedAt = { $null: true }
      } else {
        delete params.filters.publishedAt
      }
    } else if (key === "locale") {
      params.locale = value
    } else if (!excludedKeys.includes(key)) {
      if (value) {
        if (key === "pageType") {
          params.filters["page"] = { $eq: value }
        } else if (key === "domain") {
          const bannerFilters = params.filters as Record<string, unknown>
          bannerFilters.website = {
            domain: { $containsi: value },
          }
        } else if (key === "page") {
          params.filters[key] = { $eq: value }
        } else {
          params.filters[key] = { $containsi: value }
        }
      }
    } else if (value) {
      params.pagination = {
        ...params.pagination,
        [key]: value,
      }
    }
  })

  try {
    const query: GetBannersQuery & Record<string, unknown> = {
      populate: params.populate,
      sort: params.sort,
      locale: params.locale,
    }

    if (params.pagination?.pageSize) {
      query["pagination[pageSize]"] = parseInt(params.pagination.pageSize)
    }
    if (params.pagination?.page) {
      query["pagination[page]"] = parseInt(params.pagination.page)
    }

    if (params.filters && Object.keys(params.filters).length > 0) {
      Object.entries(params.filters).forEach(([key, value]) =>
        appendFilterToQuery(query as Record<string, unknown>, value, `filters[${key}]`),
      )
    }

    const requestParams: GetBannersParams = {
      next: { revalidate: REVALIDATE_TIME },
    } as RequestParams & { next?: { revalidate: number } }

    const response = await bannersApi.getBanners(query, requestParams)

    return {
      data: (response.data?.data || []) as BannerData[],
      meta: response.data?.meta,
    }
  } catch (error) {
    console.error("Failed to fetch banners:", error)
    if (typeof window !== "undefined") {
      message.error("Không thể lấy danh sách banner. Vui lòng thử lại.")
    }
    return {
      data: [],
      meta: undefined,
    }
  }
}

export const fetchBannerId = async (id: number, locale?: string): Promise<BannerData> => {
  const localeParam = safeReadLocale(locale)
  const requestParams: RequestParams & {
    query?: { populate?: string; locale?: string }
    next?: { revalidate: number }
  } = {
    query: {
      populate: "*",
      locale: localeParam,
    },
    next: { revalidate: REVALIDATE_TIME },
  }
  const response = await bannersApi.getBannersId(id, requestParams)

  if (!response.data?.data) {
    throw new Error("Banner not found")
  }

  return ensureBannerDataIntegrity(response.data.data as BannerData)
}

export const deleteBanner = async (id: number) => {
  return await bannersApi.deleteBannersId(id)
}

export const updateBanner = async (id: number, data: BannerRequest) => {
  return await bannersApi.putBannersId(id, data)
}

export const addBanner = async (data: BannerRequest) => {
  return await bannersApi.postBanners(data)
}

let copyCounter = 1

export const duplicateBanner = async (id: number) => {
  const originalBanner = await fetchBannerId(id)

  const baseName = originalBanner.attributes.name.replace(/\s+copy\s+\d+$/, "")

  const duplicateData = {
    data: {
      name: `${baseName} copy ${copyCounter++}`,
      description: originalBanner.attributes.description || undefined,
      position: originalBanner.attributes.position,
      page: originalBanner.attributes.page,
      url: originalBanner.attributes.url,
      order: originalBanner.attributes.order,
      target: originalBanner.attributes.target,
      banner: originalBanner.attributes.banner?.data?.id || 0,
      ...(originalBanner.attributes.mobileBanner?.data?.id && {
        mobileBanner: originalBanner.attributes.mobileBanner.data.id,
      }),
      ...(originalBanner.attributes.website?.data?.id && { website: originalBanner.attributes.website.data.id }),
    },
  }

  return await bannersApi.postBanners(duplicateData as BannerRequest)
}

export const checkBannerSlug = async (slug: string, locale?: string) => {
  const localeParam = safeReadLocale(locale)
  const query: GetBannersQuery & Record<string, unknown> = {
    locale: localeParam,
    "filters[slug][$eq]": slug,
  }
  const requestParams: GetBannersParams = {
    next: { revalidate: REVALIDATE_TIME },
  } as RequestParams & { next?: { revalidate: number } }
  return await bannersApi.getBanners(query, requestParams)
}

interface FetchBannerOptionsParams {
  pageSize?: string
  websitePublicationState?: "live" | "preview"
  bannerPublicationState?: "live" | "preview"
  websiteFields?: string[]
  bannerFields?: string[]
}

export const fetchBannerOptions = async (params: FetchBannerOptionsParams = {}): Promise<BannerOptions> => {
  const {
    pageSize = "100",
    websitePublicationState = "live",
    bannerPublicationState = "preview",
    websiteFields = ["name", "domain"],
    bannerFields = ["position", "page"],
  } = params

  try {
    let websitesResponse
    try {
      websitesResponse = await AdminHttpClient.get(API_PREFIX_WEBSITE_PATH, {
        params: {
          publicationState: websitePublicationState,
          "pagination[pageSize]": pageSize,
          fields: websiteFields,
        },
      })
    } catch (websiteError) {
      console.error("Failed to fetch websites:", websiteError)
      throw websiteError
    }

    let bannersResponse
    try {
      const bannersQuery: Record<string, unknown> = {
        "pagination[pageSize]": parseInt(pageSize),
      }
      bannerFields.forEach((fieldValue, idx) => {
        bannersQuery[`fields[${idx}]`] = fieldValue
      })

      const requestParams: GetBannersParams = {
        next: { revalidate: REVALIDATE_TIME },
      } as RequestParams & { next?: { revalidate: number } }

      bannersResponse = await bannersApi.getBanners(bannersQuery as GetBannersQuery, requestParams)
    } catch (bannerError) {
      console.error("Failed to fetch banners:", bannerError)
      throw bannerError
    }

    const websiteOptions = processWebsiteOptions(websitesResponse)

    const positionOptions = extractUniquePositions({
      data: (bannersResponse.data?.data || []) as BannerData[],
      meta: bannersResponse.data?.meta,
    } as ApiResponse<BannerData>)

    const result = {
      pageOptions: getFallbackBannerOptions().pageOptions,
      positionOptions,
      websiteOptions,
      targetOptions: getDefaultTargetOptions(),
    }

    return result
  } catch (error) {
    let errorMessage = "Không thể tải tùy chọn biểu mẫu. Vui lòng thử lại."
    if (error && typeof error === "object" && "response" in error) {
      const response = (error as any).response
      if (response?.status === 400 && response?.data?.error?.message) {
        const apiMessage = response.data.error.message
        if (apiMessage.includes("Invalid parameter")) {
          errorMessage = "Lỗi tham số API. Vui lòng liên hệ quản trị viên."
        } else {
          errorMessage = `Lỗi API: ${apiMessage}`
        }
      }
    }

    message.error(errorMessage)

    return getFallbackBannerOptions()
  }
}
